import { useState } from 'react';
import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown';
import Calendar from '@acko-tech/building-blocks.ui.calendar';
import * as Styles from './styles';
import { FilterTag, LEDGER_TAG_TABS } from '../constants/types';
import { useViewport } from 'utils/responsive';
import {
    getCurrentMonthInput,
    getLastTwelveMonthsOptions,
    AGGREGATE_BY_OPTIONS,
    getMonthDateRange,
} from './helpers';

type FiltersSectionProps = {
    activeTag: FilterTag;
    onGroupByTag: (tag: FilterTag) => void;
    startDate: Date;
    endDate: Date;
    setStartDate: (date: Date) => void;
    setEndDate: (date: Date) => void;
    onApplyFilters: (payload: {
        startDate: Date;
        endDate: Date;
        txnAggLevel: 'day' | 'month';
    }) => void;
};

const FiltersSection = ({
    activeTag,
    onGroupByTag,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    onApplyFilters,
}: FiltersSectionProps) => {
    const { isMobile } = useViewport();
    const isDesktop = !isMobile;
    const [aggregateBy, setAggregateBy] = useState(AGGREGATE_BY_OPTIONS[0]);
    const monthOptions = getLastTwelveMonthsOptions();
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthInput());
    const today = new Date();
    const minDate = new Date();
    minDate.setMonth(today.getMonth() - 4);
    const selectedMonthOption =
        monthOptions.find((option) => option.value === selectedMonth) ||
        monthOptions[0];

    /** From: minDate → min(endDate, today). To: startDate → today. */
    const fromCalendarEnd = new Date(
        Math.min(endDate.getTime(), today.getTime())
    );

    const renderAggregateByDropdown = () => (
        <Dropdown
            className="aggregateByDropdown"
            value={aggregateBy}
            heading={'Aggregate by'}
            label={''}
            options={AGGREGATE_BY_OPTIONS}
            onChange={(selected) => setAggregateBy(selected)}
        />
    );

    const renderDateRangeFields = () => (
        <Styles.Date>
            <Styles.CalendarWrapper>
                <Calendar
                    title="From"
                    defaultDate={startDate}
                    onSelect={(date) => {
                        setStartDate(date);
                    }}
                    enableWindow={{
                        start: minDate,
                        current: startDate,
                        end: fromCalendarEnd,
                    }}
                    isDesktop={isDesktop}
                />
            </Styles.CalendarWrapper>
            <Styles.CalendarWrapper>
                <Calendar
                    title="To"
                    defaultDate={endDate}
                    onSelect={(date) => {
                        setEndDate(date);
                    }}
                    enableWindow={{
                        start: startDate,
                        current: endDate,
                        end: today,
                    }}
                    isDesktop={isDesktop}
                />
            </Styles.CalendarWrapper>
        </Styles.Date>
    );

    const renderAggregateFields = () => {
        if (aggregateBy.value === 'day') {
            return renderDateRangeFields();
        }

        return (
            <Styles.FilterField>
                <Styles.FilterFieldLabel>month</Styles.FilterFieldLabel>
                <Dropdown
                    className="monthDropdown"
                    value={selectedMonthOption}
                    heading={'Month'}
                    label={''}
                    options={monthOptions}
                    onChange={(selected) => setSelectedMonth(selected.value)}
                />
            </Styles.FilterField>
        );
    };

    const handleApplyClick = () => {
        if (aggregateBy.value === 'day') {
            onApplyFilters({
                startDate,
                endDate,
                txnAggLevel: 'day',
            });
            return;
        }
        const { start, end } = getMonthDateRange(selectedMonth);
        onApplyFilters({
            startDate: start,
            endDate: end,
            txnAggLevel: 'month',
        });
    };

    return (
        <Styles.FilterCard>
            <Styles.DesktopTopRow>
                <Styles.TabRow>
                    {LEDGER_TAG_TABS.map((tab) => (
                        <Styles.TabButton
                            key={tab.value}
                            isActive={activeTag === tab.value}
                            onClick={() => onGroupByTag(tab.value)}
                        >
                            {tab.label}
                        </Styles.TabButton>
                    ))}
                </Styles.TabRow>
            </Styles.DesktopTopRow>
            {isMobile ? (
                <Styles.MobileControlsWrapper>
                    <Styles.MobileTopRow>
                        <Styles.MobileAggregateLabel>
                            Aggregate by
                        </Styles.MobileAggregateLabel>
                        <Styles.MobileAggregateField>
                            {renderAggregateByDropdown()}
                        </Styles.MobileAggregateField>
                    </Styles.MobileTopRow>
                    <Styles.MobileBottomRow>
                        {aggregateBy.value === 'day'
                            ? renderDateRangeFields()
                            : renderAggregateFields()}
                    </Styles.MobileBottomRow>
                    <Styles.MobileApplyRow>
                        <Styles.MobileApplyFiltersButton
                            type="button"
                            onClick={handleApplyClick}
                        >
                            Apply
                        </Styles.MobileApplyFiltersButton>
                    </Styles.MobileApplyRow>
                </Styles.MobileControlsWrapper>
            ) : (
                <Styles.FilterControls className="inlineControls">
                    <Styles.FiltersLabel>Aggregate By</Styles.FiltersLabel>
                    <Styles.FilterField>
                        {renderAggregateByDropdown()}
                    </Styles.FilterField>
                    {renderAggregateFields()}
                    <Styles.ApplyFiltersButton
                        type="button"
                        onClick={handleApplyClick}
                    >
                        Apply
                    </Styles.ApplyFiltersButton>
                </Styles.FilterControls>
            )}
        </Styles.FilterCard>
    );
};

export default FiltersSection;
